import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import MarkerPill from '../../../shared/components/pill/MarkerPill';
import { getIntegrationManifest } from '../../../integrations';
import IntegrationSettingsForm from './IntegrationSettingsForm.jsx';
import IntegrationSyncLogPanel from './IntegrationSyncLogPanel.jsx';
import IntegrationActivityPanel from './IntegrationActivityPanel.jsx';
import IntegrationHealthPanel from './IntegrationHealthPanel.jsx';

// Tabs depend on what the integration actually does. A sync log is meaningless for a ticketing
// connector, which has no assets and no sync; an outbound delivery log is meaningless for an IoT
// one. Driven by the roles the backend reports rather than by the integration type, so a second
// connector of either kind needs no change here.
function tabsFor(detail) {
  const roles = detail?.roles || [];
  const tabs = [{ key: 'settings', label: 'Settings' }];
  if (roles.includes('iot_backend')) tabs.push({ key: 'sync_log', label: 'Sync log' });
  if (roles.includes('upstream_ticketing')) {
    tabs.push({ key: 'activity', label: 'Activity' }, { key: 'health', label: 'Health' });
  }
  return tabs;
}

export default function IntegrationDetailSidebar({
  show,
  detail,
  detailLoading,
  saving,
  syncing,
  onClose,
  onSave,
  onSync,
}) {
  const [activeTab, setActiveTab] = useState('settings');
  const tabs = tabsFor(detail);
  // A tab can vanish when a different integration is opened, so fall back rather than render
  // an empty panel.
  const currentTab = tabs.some((tab) => tab.key === activeTab) ? activeTab : 'settings';

  if (!show) return null;

  const manifest = detail ? getIntegrationManifest(detail.type) : null;
  const title = detail?.display_name || 'Integration';

  return createPortal(
    <>
      <div className="offcanvas-backdrop fade show" onClick={onClose} aria-hidden="true" />
      <div
        className="offcanvas offcanvas-end show"
        tabIndex="-1"
        style={{ visibility: 'visible', width: 'min(480px, 100vw)' }}
        aria-modal="true"
        role="dialog"
      >
        <div className="offcanvas-header border-bottom">
          <div className="d-flex align-items-center gap-3 overflow-hidden">
            {manifest?.logo ? (
              <img src={manifest.logo} alt={title} style={{ width: 32, height: 32 }} />
            ) : (
              <i className="bi bi-plug fs-4 text-secondary" />
            )}
            <div className="overflow-hidden">
              <h5 className="offcanvas-title mb-0 text-truncate">{title}</h5>
              {detail?.name && (
                <div className="small text-muted text-truncate">{detail.name}</div>
              )}
            </div>
          </div>
          <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
        </div>

        <div className="offcanvas-body d-flex flex-column p-0">
          <ul className="nav nav-tabs px-3 pt-2 border-bottom-0">
            {tabs.map((tab) => (
              <li className="nav-item" key={tab.key}>
                <button
                  type="button"
                  className={classNames('nav-link', { active: currentTab === tab.key })}
                  onClick={() => setActiveTab(tab.key)}
                >
                  {tab.label}
                  {tab.key === 'sync_log' && detail?.latest_sync_run?.status === 'failed' && (
                    <MarkerPill className="ms-1" title="The last sync run failed">!</MarkerPill>
                  )}
                  {tab.key === 'health' && ['degraded', 'failed'].includes(detail?.health_status) && (
                    <MarkerPill
                      tone={detail.health_status === 'failed' ? 'danger' : 'warning'}
                      className="ms-1"
                      title={`This integration is ${detail.health_status}`}
                    >
                      !
                    </MarkerPill>
                  )}
                </button>
              </li>
            ))}
          </ul>

          <div className="flex-grow-1 overflow-auto p-3">
            {detailLoading && !detail ? (
              <div className="text-muted small py-4 text-center">Loading integration…</div>
            ) : (
              <>
                {currentTab === 'settings' && (
                  <IntegrationSettingsForm
                    detail={detail}
                    saving={saving}
                    onSave={onSave}
                  />
                )}
                {currentTab === 'sync_log' && (
                  <>
                    <div className="d-flex justify-content-end mb-3">
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        onClick={onSync}
                        disabled={syncing || !detail?.roles?.includes('iot_backend')}
                      >
                        {syncing ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                            Syncing…
                          </>
                        ) : (
                          <>
                            <i className="bi bi-arrow-repeat me-1" />
                            Sync now
                          </>
                        )}
                      </button>
                    </div>
                    <IntegrationSyncLogPanel
                      syncRun={detail?.latest_sync_run}
                      loading={detailLoading}
                    />
                  </>
                )}
                {currentTab === 'activity' && (
                  <IntegrationActivityPanel integrationId={detail?.id} />
                )}
                {currentTab === 'health' && detail && (
                  <IntegrationHealthPanel detail={detail} />
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}

IntegrationDetailSidebar.propTypes = {
  show: PropTypes.bool.isRequired,
  detail: PropTypes.object,
  detailLoading: PropTypes.bool,
  saving: PropTypes.bool,
  syncing: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onSync: PropTypes.func.isRequired,
};
